import api from './api';

export type ObservabilityHealth =
  'healthy' | 'degraded' | 'critical' | 'unknown';

export type ObservabilitySeverity = 'critical' | 'warning' | 'info';

export type ObservabilitySourceStatus =
  | 'ok'
  | 'partial'
  | 'stale'
  | 'error'
  | 'unavailable'
  | 'not_configured'
  | 'forbidden';

export type ObservabilityRange =
  '15m' | '1h' | '6h' | '24h' | '7d' | '14d' | 'custom';

export const OBSERVABILITY_RETENTION_MILLISECONDS = 14 * 24 * 60 * 60 * 1000;
export const OBSERVABILITY_MINIMUM_REFETCH_MILLISECONDS = 30 * 1000;

export function getObservabilityRefetchInterval(interval: number | false) {
  return interval === false
    ? false
    : Math.max(interval, OBSERVABILITY_MINIMUM_REFETCH_MILLISECONDS);
}

export type MetricUnit = 'count' | 'ratio' | 'milliseconds' | 'bytes' | 'cores';

export type MetricSeries = {
  key: string;
  label: string;
  unit: MetricUnit;
  points: Array<{ at: string; value: number | null }>;
};

export type ObservabilitySource = {
  id: 'hatchet' | 'prometheus' | 'kubernetes' | 'alerts' | 'watchdog';
  status: ObservabilitySourceStatus;
  observedAt?: string;
  code?: string;
  message?: string;
};

export type ObservabilitySignal = {
  id: string;
  severity: ObservabilitySeverity;
  title: string;
  summary: string;
  startedAt?: string;
  runId?: string;
  workflowId?: string;
  workerId?: string;
};

export type ObservabilityEnvelope<T> = {
  data: T;
  meta: {
    generatedAt: string;
    partial: boolean;
    range: {
      from: string;
      to: string;
      stepSeconds: number;
    };
    sources: ObservabilitySource[];
  };
};

export type ObservabilityCapabilities = {
  tenantOverviewRead: boolean;
  platformInfrastructureRead: boolean;
  alertSilenceCreate: boolean;
  csrfToken?: string;
};

export type ObservabilityAlert = {
  fingerprint: string;
  status: 'firing' | 'resolved' | 'silenced';
  severity: ObservabilitySeverity;
  title: string;
  summary: string;
  startedAt: string;
  resolvedAt?: string;
  silencedUntil?: string;
  tenantId?: string;
  resource?: string;
  runId?: string;
  workflowId?: string;
  workerId?: string;
};

export type ObservabilityIncident = {
  id: string;
  target: string;
  status: 'open' | 'resolved';
  severity: ObservabilitySeverity;
  title: string;
  summary: string;
  startedAt: string;
  resolvedAt?: string;
  alerts: string[];
};

export type ObservabilityAuditEvent = {
  id: string;
  actorUserId: string;
  action: string;
  resource: string;
  reason: string | null;
  durationMinutes: number | null;
  silenceId: string | null;
  createdAt: string;
};

export type TenantQueueHealth = {
  id: string;
  name: string;
  workflowId?: string;
  queued: number;
  running: number | null;
  oldestQueuedAgeMs: number | null;
  throughputPerMinute: number | null;
  queueWaitP95Ms: number | null;
  severity: ObservabilitySeverity | 'healthy';
};

export type TenantWorkflowHealth = {
  id: string;
  name: string;
  workflowId?: string;
  taskRuns: number;
  successRate: number | null;
  durationP95Ms: number | null;
  queueWaitP95Ms: number | null;
  queued: number;
  severity: ObservabilitySeverity | 'healthy';
};

export type TenantWorkerHealth = {
  id: string;
  name: string;
  status: 'ACTIVE' | 'PAUSED' | 'INACTIVE';
  lastHeartbeatAt?: string;
  usedSlots: number;
  totalSlots: number;
  labels: string[];
};

export type TenantProblemRun = {
  id: string;
  displayName: string;
  workflowName?: string;
  status: 'QUEUED' | 'RUNNING' | 'FAILED';
  createdAt: string;
  durationMs: number | null;
  error?: string;
};

export type TenantOverview = {
  health: ObservabilityHealth;
  signals: ObservabilitySignal[];
  rangeSummary: {
    taskRuns: number | null;
    successRate: number | null;
    throughputPerMinute: number | null;
    queueWaitP95Ms: number | null;
  };
  currentQueue: {
    queued: number | null;
    running: number | null;
    pending: number | null;
    oldestQueuedAgeMs: number | null;
  };
  currentWorkers: {
    active: number | null;
    paused: number | null;
    inactive: number | null;
    usedSlots: number | null;
    totalSlots: number | null;
  };
  resources: {
    cpuUsedCores: number | null;
    cpuRequestedCores: number | null;
    memoryUsedBytes: number | null;
    memoryRequestedBytes: number | null;
    restarts: number | null;
    oomKills: number | null;
    podsReady: number | null;
    podsTotal: number | null;
    deploymentsReady: number | null;
    deploymentsTotal: number | null;
  };
  series: {
    executions: MetricSeries[];
    queue: MetricSeries[];
    latency: MetricSeries[];
    capacity: MetricSeries[];
    resources: MetricSeries[];
  };
  queues: TenantQueueHealth[];
  workflows: TenantWorkflowHealth[];
  workers: TenantWorkerHealth[];
  problemRuns: TenantProblemRun[];
};

export type InfrastructureNode = {
  kind: 'node';
  uid: string;
  name: string;
  status: string;
  roles: string[];
  zone?: string;
  version?: string;
  cpuUsedCores: number | null;
  cpuAllocatableCores: number;
  memoryUsedBytes: number | null;
  memoryAllocatableBytes: number;
  filesystemUsedRatio: number | null;
  pods: number;
  podCapacity: number;
  conditions: string[];
};

export type InfrastructureWorkload = {
  kind: 'workload';
  uid: string;
  namespace: string;
  name: string;
  workloadKind: 'Deployment' | 'StatefulSet' | 'DaemonSet' | 'Job' | 'CronJob';
  status: string;
  desired: number;
  ready: number;
  available: number;
  restarts: number;
  cpuUsedCores: number | null;
  memoryUsedBytes: number | null;
};

export type InfrastructurePod = {
  kind: 'pod';
  uid: string;
  namespace: string;
  name: string;
  owner?: string;
  node?: string;
  status: string;
  readyContainers: number;
  totalContainers: number;
  restarts: number;
  lastTerminationReason?: string;
  cpuUsedCores: number | null;
  memoryUsedBytes: number | null;
};

export type InfrastructureVolume = {
  kind: 'volume';
  uid: string;
  namespace: string;
  name: string;
  status: string;
  storageClass?: string;
  requestedBytes: number;
  capacityBytes: number | null;
  usedRatio: number | null;
  persistentVolume?: string;
};

export type InfrastructureService = {
  kind: 'service';
  uid: string;
  name: string;
  namespace?: string;
  status: string;
  readyReplicas: number | null;
  desiredReplicas: number | null;
  restarts: number | null;
  requestRate: number | null;
  errorRate: number | null;
  latencyP95Ms: number | null;
};

export type InfrastructureResource =
  | InfrastructureNode
  | InfrastructureWorkload
  | InfrastructurePod
  | InfrastructureVolume
  | InfrastructureService;

export type InfrastructureEvent = {
  id: string;
  at: string;
  type: 'Normal' | 'Warning';
  reason: string;
  resourceKind: string;
  resourceName: string;
  resourceUid?: string;
  namespace?: string;
  count: number;
  message: string;
};

export type InfrastructureOverview = {
  cluster: {
    id: string;
    name: string;
    version?: string;
  };
  health: ObservabilityHealth;
  signals: ObservabilitySignal[];
  summary: {
    nodes: { ready: number | null; total: number | null };
    pods: {
      ready: number | null;
      total: number | null;
      failed: number | null;
    };
    workloads: {
      available: number | null;
      total: number | null;
      unavailable: number | null;
    };
    cpu: {
      usedCores: number | null;
      requestedCores: number | null;
      allocatableCores: number | null;
    };
    memory: {
      usedBytes: number | null;
      requestedBytes: number | null;
      allocatableBytes: number | null;
    };
    restarts: number | null;
  };
  series: {
    capacity: MetricSeries[];
    pods: MetricSeries[];
    storage: MetricSeries[];
  };
  unhealthyWorkloads: InfrastructureWorkload[];
  unhealthyPods: InfrastructurePod[];
  services: InfrastructureService[];
  events: InfrastructureEvent[];
  namespaces: string[];
};

export type InfrastructureResources = {
  rows: InfrastructureResource[];
  nextCursor?: string;
  namespaces: string[];
};

export type InfrastructureResourceDetail = {
  resource: InfrastructureResource;
  metrics: MetricSeries[];
  conditions: Array<{
    type: string;
    status: string;
    reason?: string;
    message?: string;
    changedAt?: string;
  }>;
  events: InfrastructureEvent[];
  related: InfrastructureResource[];
};

const rangeMilliseconds: Record<
  Exclude<ObservabilityRange, 'custom'>,
  number
> = {
  '15m': 15 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '14d': OBSERVABILITY_RETENTION_MILLISECONDS,
};

export function getObservabilityWindow(
  range: ObservabilityRange,
  customFrom?: string,
  customTo?: string,
) {
  if (range === 'custom') {
    if (!customFrom || !customTo) {
      throw new Error('Custom observability ranges require from and to');
    }

    const fromMilliseconds = Date.parse(customFrom);
    const toMilliseconds = Date.parse(customTo);
    if (
      !Number.isFinite(fromMilliseconds) ||
      !Number.isFinite(toMilliseconds) ||
      fromMilliseconds >= toMilliseconds ||
      toMilliseconds - fromMilliseconds > rangeMilliseconds['14d']
    ) {
      throw new Error(
        'Custom observability ranges must be valid, positive, and at most 14 days',
      );
    }

    return { from: customFrom, to: customTo };
  }

  const to = new Date();
  return {
    from: new Date(to.getTime() - rangeMilliseconds[range]).toISOString(),
    to: to.toISOString(),
  };
}

const basePath = '/api/observability/v1';

export const observabilityCapabilitiesQuery = {
  queryKey: ['observability', 'capabilities'] as const,
  queryFn: async () =>
    (
      await api.instance.get<ObservabilityEnvelope<ObservabilityCapabilities>>(
        `${basePath}/capabilities`,
      )
    ).data.data,
  staleTime: 5 * 60 * 1000,
  refetchInterval: 30 * 60 * 1000,
  retry: false,
};

export function tenantOverviewQuery(
  tenantId: string,
  range: ObservabilityRange,
  customFrom?: string,
  customTo?: string,
) {
  return {
    queryKey: [
      'observability',
      'tenant',
      tenantId,
      range,
      customFrom,
      customTo,
    ],
    queryFn: async () => {
      const window = getObservabilityWindow(range, customFrom, customTo);
      return (
        await api.instance.get<ObservabilityEnvelope<TenantOverview>>(
          `${basePath}/tenants/${encodeURIComponent(tenantId)}/overview`,
          { params: window },
        )
      ).data;
    },
  };
}

export function infrastructureOverviewQuery(
  range: ObservabilityRange,
  customFrom?: string,
  customTo?: string,
) {
  return {
    queryKey: ['observability', 'infrastructure', range, customFrom, customTo],
    queryFn: async () => {
      const window = getObservabilityWindow(range, customFrom, customTo);
      return (
        await api.instance.get<ObservabilityEnvelope<InfrastructureOverview>>(
          `${basePath}/platform/infrastructure/overview`,
          { params: window },
        )
      ).data;
    },
  };
}

export function infrastructureResourcesQuery(
  kind: InfrastructureResource['kind'],
  namespace?: string,
  status?: string,
  search?: string,
) {
  return {
    queryKey: [
      'observability',
      'infrastructure-resources',
      kind,
      namespace,
      status,
      search,
    ],
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      (
        await api.instance.get<ObservabilityEnvelope<InfrastructureResources>>(
          `${basePath}/platform/infrastructure/resources`,
          {
            params: {
              kind,
              namespace,
              status,
              q: search,
              cursor: pageParam,
              limit: 100,
            },
          },
        )
      ).data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (
      lastPage: ObservabilityEnvelope<InfrastructureResources>,
    ) => lastPage.data.nextCursor,
  };
}

export function infrastructureEventsQuery(
  range: ObservabilityRange,
  namespace?: string,
  search?: string,
  customFrom?: string,
  customTo?: string,
) {
  return {
    queryKey: [
      'observability',
      'infrastructure-events',
      range,
      namespace,
      search,
      customFrom,
      customTo,
    ],
    queryFn: async () => {
      const window = getObservabilityWindow(range, customFrom, customTo);
      return (
        await api.instance.get<
          ObservabilityEnvelope<{ rows: InfrastructureEvent[] }>
        >(`${basePath}/platform/infrastructure/events`, {
          params: { ...window, namespace, q: search, limit: 200 },
        })
      ).data;
    },
  };
}

export function infrastructureResourceDetailQuery(
  kind: InfrastructureResource['kind'],
  uid: string,
  range: ObservabilityRange,
  customFrom?: string,
  customTo?: string,
) {
  return {
    queryKey: [
      'observability',
      'infrastructure-resource',
      kind,
      uid,
      range,
      customFrom,
      customTo,
    ],
    queryFn: async () => {
      const window = getObservabilityWindow(range, customFrom, customTo);
      return (
        await api.instance.get<
          ObservabilityEnvelope<InfrastructureResourceDetail>
        >(
          `${basePath}/platform/infrastructure/resources/${kind}/${encodeURIComponent(uid)}`,
          { params: window },
        )
      ).data;
    },
  };
}

export function observabilityAlertsQuery(
  scope: 'tenant' | 'platform',
  tenantId?: string,
) {
  return {
    queryKey: ['observability', 'alerts', scope, tenantId],
    queryFn: async () =>
      (
        await api.instance.get<
          ObservabilityEnvelope<{ rows: ObservabilityAlert[] }>
        >(`${basePath}/alerts`, { params: { scope, tenantId } })
      ).data,
  };
}

export function observabilityIncidentsQuery() {
  return {
    queryKey: ['observability', 'incidents'],
    queryFn: async () =>
      (
        await api.instance.get<
          ObservabilityEnvelope<{ rows: ObservabilityIncident[] }>
        >(`${basePath}/incidents`)
      ).data,
  };
}

export function observabilityAuditEventsQuery() {
  return {
    queryKey: ['observability', 'audit-events'],
    queryFn: async () =>
      (
        await api.instance.get<
          ObservabilityEnvelope<{ rows: ObservabilityAuditEvent[] }>
        >(`${basePath}/audit-events`, { params: { limit: 50 } })
      ).data,
  };
}

export async function silenceObservabilityAlert(
  fingerprint: string,
  durationMinutes: 15 | 60 | 240 | 1440,
  reason: string,
  csrfToken: string,
  idempotencyKey: string,
) {
  return (
    await api.instance.post(
      `${basePath}/alerts/${encodeURIComponent(fingerprint)}/silences`,
      { durationMinutes, reason, idempotencyKey },
      { headers: { 'x-observability-csrf': csrfToken } },
    )
  ).data;
}
